'use server';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';

import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';
import { EDIT_FORBIDDEN, guardIllustrationEdit } from '@/contexts/pipeline-v2/edits/guardAction.server';
import type { Illustration, IllustrationStyle, IllustrationFormat, CharacterDecision } from '@/contexts/illustrations/types';

export async function updateIllustration(
  bookId: string,
  updates: {
    title?: string;
    style?: string;
    format?: string;
    status?: string;
    characterDecisions?: Array<{
      extractedName: string;
      action: string;
      matchedCharacterId?: string;
      createData?: { name: string; description?: string };
      skipConfirmed?: boolean;
    }>;
    coverReferenceCharacters?: string[];
  },
): Promise<ActionResult<{ book: Illustration }>> {
  try {
    const familyId = await SessionService.getFamilyId();
    const userId = await SessionService.getUserId();
    if (!familyId || !userId) return { success: false, error: 'Not authenticated' };

    // edits after ready: who is on the cover, the title; a new format / style is a new illustration
    const { getIllustrationRepository: illustrationsRepo } = await import('@/contexts/illustrations/repositories');
    const current = await illustrationsRepo().getById(bookId, familyId);
    const drawn = (current?.pages ?? []).some((p) => !!(p as { imageUrl?: string | null }).imageUrl);
    const kind = drawn && ((updates.style && updates.style !== current?.style) || (updates.format && updates.format !== current?.format))
      ? 'format_style' as const
      : updates.coverReferenceCharacters ? 'cover_cast' as const
        : updates.title !== undefined ? 'title' as const : null;
    if (kind) {
      const blocked = await guardIllustrationEdit({ illustrationId: bookId, familyId, kind });
      if (blocked) return { success: false, error: blocked.message, code: EDIT_FORBIDDEN };
    }

    const { IllustrationService } = await import('@/contexts/illustrations/services/BookService');
    const { getIllustrationRepository } = await import('@/contexts/illustrations/repositories');
    const { getStoryRepository } = await import('@/contexts/stories/repositories');
    const { getCharacterRepository } = await import('@/contexts/characters/repositories');

    const illustrationService = new IllustrationService(getIllustrationRepository(), getStoryRepository(), getCharacterRepository());

    const result = await illustrationService.updateIllustration(bookId, familyId, {
      title: updates.title,
      style: updates.style ? updates.style as IllustrationStyle : undefined,
      format: updates.format ? updates.format as IllustrationFormat : undefined,
      status: updates.status ? updates.status as Illustration['status'] : undefined,
      characterDecisions: updates.characterDecisions?.map(d => ({
        extractedName: d.extractedName,
        action: d.action as CharacterDecision['action'],
        matchedCharacterId: d.matchedCharacterId || undefined,
        createData: d.createData || undefined,
        skipConfirmed: d.skipConfirmed || undefined,
      })),
      coverReferenceCharacters: updates.coverReferenceCharacters,
    });

    if (!result.success || !result.book) {
      return { success: false, error: result.error || 'UPDATE_FAILED' };
    }

    return { success: true, data: { book: result.book } };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('updateBook', e) };
  }
}
